import api from '../api';
import keycloak from '../../keycloak';
import { postSseStream } from '../sseClient';
const baseUrl = process.env.REACT_APP_AUTH_API_URL;

const createBrainstorm = async (projectId, payload, keycloakToken) => {
    try {
        const response = await api.post(`${baseUrl}/brainstorm/${projectId}`, payload, {
            headers: {
                'Authorization': `Bearer ${keycloakToken}`,
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error at creating brainstorm:", error);
        throw new Error(error?.response?.data?.message || "Erro ao criar brainstorm.");
    }
}


const updateBrainstorm = async (outputId, payload, keycloakToken) => {
    try {
        const response = await api.put(`${baseUrl}/brainstorm/${outputId}`, payload, {
            headers: {
                'Authorization': `Bearer ${keycloakToken}`,
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error at updating brainstorm:", error);
        throw new Error(error?.response?.data?.message || "Erro ao atualizar brainstorm.");
    }
}

const createAssetKnowledgeBase = async (projectId, payload, fileName, keycloakToken) => {
    try {
        const response = await api.post(`${baseUrl}/brainstorm/send-knowledge-base/${projectId}`, { data: payload, fileName }, {
            headers: {
                'Authorization': `Bearer ${keycloakToken}`,
            }
        });

        return response.data;
    } catch (error) {
        console.error("Error at creating asset knowledge base:", error);
        throw new Error(error?.response?.data?.message || "Erro ao criar asset da knowledge base.");
    }
}

const generateBrainstormChallengeIdea = async (projectId, existentQuestions, challenge, questionsNumber, keycloakToken) => {
    try {
        const response = await api.post(`${baseUrl}/brainstorm/guiding-questions/${questionsNumber}`,
        { projectId, existentQuestions, message: challenge },
        {
            headers: {
                'Authorization': `Bearer ${keycloakToken}`,
            }
        });
        
        return response.data;
    } catch (error) {
        console.error("Error at generating guiding questions:", error);
        throw new Error(error?.response?.data?.message || "Erro ao gerar perguntas norteadoras.");
    }
}

const generateIdeasByGuidingQuestion = async (projectId, guidingQuestion, challenge, ideasNumber, existentIdeas, keycloakToken) => {
    try {
        const response = await api.post(`${baseUrl}/brainstorm/ideias/${ideasNumber}`, { projectId, problem: guidingQuestion, challenge, existentIdeas }, {
            headers: {
                'Authorization': `Bearer ${keycloakToken}`,
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error at generating ideas:", error);
        throw new Error(error?.response?.data?.message || "Erro ao gerar ideias.");
    }
}

const renderIdeasPDF = async (data, keycloakToken) => {
    try {
        const response = await api.post(`${baseUrl}/pdf/render`, { data }, {
            headers: {
                'Authorization': `Bearer ${keycloakToken}`,
                'Content-Disposition': 'attachment; filename="ideias-brainstorm.pdf"',
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error at rendering ideas PDF:", error);
        throw new Error(error?.response?.data?.message || "Erro ao renderizar ideias em PDF.");
    }
}

const getBrainstormById = async (brainstormId, keycloakToken) => {
    try {
        const response = await api.get(`${baseUrl}/brainstorm/${brainstormId}`, {
            headers: {
                'Authorization': `Bearer ${keycloakToken}`,
            }   
        });
        return response.data;
    } catch (error) {
        console.error("Error at getting brainstorm:", error);
        throw new Error(error?.response?.data?.message || "Erro ao buscar brainstorm.");
    }
}

const generateConceptsByBrainstorm = async (projectId, keycloakToken, payload) => {
    try {
        const response = await api.post(`${baseUrl}/brainstorm/concepts/${projectId}`, payload, {
            headers: {
                'Authorization': `Bearer ${keycloakToken}`,
            }
        });
        return response.data.response;
    }
    catch (error) {
        console.error("Error at generating concepts by brainstorm:", error);
        throw new Error(error?.response?.data?.message || "Erro ao gerar conceitos por brainstorm.");
    }
}

/**
 * Consome a geração de conceitos em streaming: cada conceito chega assim que fica pronto.
 *
 * Não usa o `api` (Axios) porque Axios não expõe streaming no browser. Como o interceptor
 * de renovação de token vive no Axios, o refresh é feito manualmente aqui.
 */
const generateConceptsStream = async (projectId, payload, { signal, onStart, onConcept, onConceptError, onStreamError, onDone } = {}) => {
    await keycloak.updateToken(30);

    // `error` e `concept-error` têm payloads diferentes: o global não traz `index`.
    // Compartilhar handler fazia a falha do stream inteiro ser engolida em silêncio.
    const handlers = {
        start: onStart,
        concept: onConcept,
        'concept-error': onConceptError,
        error: onStreamError,
        done: onDone,
    };

    await postSseStream(`${baseUrl}/brainstorm/concepts/${projectId}/stream`, {
        token: keycloak.token,
        body: payload,
        signal,
        onEvent: ({ event, data }) => {
            const handler = handlers[event];
            if (handler) handler(data);
        },
    });
}

const deleteFileFromS3 = async (fileUrl, keycloakToken) => {
    try {
        const response = await api.delete(`${baseUrl}/file/delete`, {
            headers: { 'Authorization': `Bearer ${keycloakToken}` },
            data: { url: fileUrl }
        });
        return response.data;
    } catch (error) {
        console.error("Error deleting file from S3:", error);
        throw new Error(error?.response?.data?.message || "Erro ao deletar arquivo.");
    }
}

const signImages = async (ideas, keycloakToken) => {
    try {
        const response = await api.post(`${baseUrl}/brainstorm/concept-images-signing`, { ideas, challenges: [], guidingQuestions: [] }, {
            headers: { 'Authorization': `Bearer ${keycloakToken}` }
        });
        return response.data;
    } catch (error) {
        console.error("Error signing images:", error);
        throw new Error(error?.response?.data?.message || "Erro ao assinar imagens.");
    }
}

const renderConceptPdf = async (conceptData, keycloakToken) => {
    try {
        const response = await api.post(`${baseUrl}/brainstorm/concept-pdf`, conceptData, {
            headers: { 'Authorization': `Bearer ${keycloakToken}` },
            responseType: 'arraybuffer',
        });
        return response.data;
    } catch (error) {
        console.error("Error rendering concept PDF:", error);
        throw new Error(error?.response?.data?.message || "Erro ao gerar PDF do conceito.");
    }
}

export { 
    createBrainstorm,
    updateBrainstorm,
    generateBrainstormChallengeIdea,
    generateIdeasByGuidingQuestion,
    renderIdeasPDF,
    createAssetKnowledgeBase,
    getBrainstormById,
    generateConceptsByBrainstorm,
    generateConceptsStream,
    deleteFileFromS3,
    signImages,
    renderConceptPdf
};
